import { isSafeRelativeReturnUrl } from '@/lib/returnUrl'
import { serializeUserForStorage } from '@/lib/authStorage'
import { env } from '@/config/env'
import { api } from './api'

// Helper function to decode JWT token
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    )
    return JSON.parse(jsonPayload)
  } catch (error) {
    return null
  }
}

function getDecodedClaim(decodedToken: any, key: string): unknown {
  const value = decodedToken?.[key]
  return Array.isArray(value) ? value[0] : value
}

function toBooleanFlag(value: unknown): boolean {
  const normalizedValue = Array.isArray(value) ? value[0] : value
  if (typeof normalizedValue === 'boolean') return normalizedValue
  if (typeof normalizedValue === 'number') return normalizedValue === 1
  if (typeof normalizedValue === 'string') {
    return ['true', '1', 'yes', 'y'].includes(normalizedValue.trim().toLowerCase())
  }
  return false
}

const normalizeClaimName = (key: string) => key.replace(/[^a-z0-9]/gi, '').toLowerCase()

// Role claims are named after each tenant's role columns, e.g. legacy_district_register or legacy_districtregister (Region 10).
function getRoleFlag(decodedToken: any, key: string): boolean {
  if (!decodedToken || typeof decodedToken !== 'object') return false
  const normalizedKey = normalizeClaimName(key)
  return Object.keys(decodedToken).some(
    claim => normalizeClaimName(claim) === normalizedKey && toBooleanFlag(getDecodedClaim(decodedToken, claim)),
  )
}

function getLegacyRegisterRolesFromDecodedToken(decodedToken: any) {
  return {
    legacy_campus_register: getRoleFlag(decodedToken, 'legacy_campus_register'),
    legacy_district_register: getRoleFlag(decodedToken, 'legacy_district_register'),
    legacy_global_register: getRoleFlag(decodedToken, 'legacy_global_register'),
  }
}

const COMMUNITY_ADMIN_NOTIFICATION_ROLE = 'communityAdminNotification'
const ROLE_CLAIM_KEYS = [
  'role',
  'roles',
  'permissions',
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role',
  'https://schemas.microsoft.com/ws/2008/06/identity/claims/role',
] as const

function hasCommunityAdminNotificationRole(decodedToken: any): boolean {
  if (toBooleanFlag(getDecodedClaim(decodedToken, COMMUNITY_ADMIN_NOTIFICATION_ROLE))) return true

  const expectedRole = COMMUNITY_ADMIN_NOTIFICATION_ROLE.toLowerCase()
  return ROLE_CLAIM_KEYS.some(key => {
    const claim = decodedToken?.[key]
    const roles = Array.isArray(claim) ? claim : [claim]
    return roles.some(role =>
      typeof role === 'string' &&
      role.split(/[\s,]+/).some(value => value.toLowerCase() === expectedRole),
    )
  })
}

// Types
export interface LoginRequest {
  email: string
  password: string
}

export interface LoginResponse {
  success: boolean
  message?: string
  accessToken?: string
  refreshToken?: string
  user?: {
    id: string
    email: string
    firstName?: string
    lastName?: string
    emailVerified?: boolean
    isActive?: boolean
    isDeleted?: boolean
  }
  requiresEmailVerification?: boolean
}

export interface CreateUserRequest {
  email: string
  password: string
  firstName: string
  lastName: string
  middleName?: string
  address?: string
  city?: string
  state?: string
  zip?: string
  homePhone?: string
  workPhone?: string
  secondaryEmail?: string
  locationId?: number
  siteId?: number
  orgId?: number
  positionId?: number
  salutationId?: number
  gradeLevelId?: number
}

export interface CreateUserResponse {
  success: boolean
  message?: string
  userId?: string
  requiresEmailVerification?: boolean
}

export interface VerifyEmailRequest {
  email: string
  code: string
}

export interface VerifyEmailResponse {
  success: boolean
  message?: string
  accessToken?: string
  refreshToken?: string
}

export interface ResendVerificationCodeRequest {
  email: string
}

export interface ResendVerificationCodeResponse {
  success: boolean
  message?: string
}

export interface CheckUserResponse {
  success: boolean
  exists: boolean
  emailVerified?: boolean
  isActive?: boolean
  isDeleted?: boolean
}

export interface RefreshTokenRequest {
  refreshToken: string
}

export interface RefreshTokenResponse {
  success: boolean
  accessToken?: string
  refreshToken?: string
}

export interface LogoutRequest {
  refreshToken?: string
}

export interface ResetPasswordRequest {
  email: string
}

export interface CompleteAccountSetupResponse {
  success: boolean
  message?: string
  email?: string
}

export interface ChangePasswordRequest {
  oldPassword: string
  newPassword: string
}

export interface SSOValidationRequest {
  token: string
}

export interface SSOValidationResponse {
  success: boolean
  message?: string
  accessToken?: string
  refreshToken?: string
  // Backend might return user as nested object OR at root level
  user?: {
    email: string
    name?: string
    firstName?: string
    lastName?: string
    isEmployee?: boolean
    authenticatedVia?: string
  }
  // Or fields might be at root level
  email?: string
  name?: string
  firstName?: string
  lastName?: string
  isEmployee?: boolean
  authenticatedVia?: string
}

export interface SSORedirectResponse {
  success?: boolean
  message?: string
  redirectUrl: string
}

function buildEmployeeSSOExchangeUrl(): string {
  const tenant = env('VITE_TENANT_ID')
  if (!tenant) {
    throw new Error('Tenant ID is required for employee SSO.')
  }

  return `/security/${tenant}/employee-sso`
}

function buildSSORedirectUrl(returnUrl?: string): string {
  if (returnUrl && !isSafeRelativeReturnUrl(returnUrl)) {
    throw new Error('Return URL must be a relative application path.')
  }

  const queryParams = new URLSearchParams()
  if (returnUrl) {
    queryParams.set('returnUrl', returnUrl)
  }

  const queryString = queryParams.toString()
  return queryString ? `/security/generate-sso-url?${queryString}` : '/security/generate-sso-url'
}

async function requestSSORedirect(returnUrl?: string): Promise<SSORedirectResponse> {
  const response = await api.get<SSORedirectResponse>(buildSSORedirectUrl(returnUrl))

  if (response.success === false) {
    throw new Error(response.message || 'Unable to start employee SSO.')
  }

  if (!response.redirectUrl) {
    throw new Error('SSO redirect endpoint did not return a redirect URL.')
  }

  return response
}

// Auth Service
export const authService = {
  // Login with email/password
  login: async (email: string, password: string): Promise<LoginResponse> => {
    try {
      const response = await api.post<LoginResponse>('/security/login', {
        email,
        password,
      })
      
      // Store tokens if login successful
      if (response.success && response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken)
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken)
        }
        
        // Decode JWT to extract user info
        const decodedToken = decodeJWT(response.accessToken)
        
        if (decodedToken) {
          // Safely extract email - it may be an array in the JWT
          const rawEmail = Array.isArray(decodedToken.email) ? decodedToken.email[0] : decodedToken.email
          const firstName = typeof decodedToken.legacy_fname === 'string' ? decodedToken.legacy_fname : ''
          const lastName = typeof decodedToken.legacy_lname === 'string' ? decodedToken.legacy_lname : ''
          
          const legacySid = Array.isArray(decodedToken.legacy_sid)
            ? decodedToken.legacy_sid[0]
            : decodedToken.legacy_sid || ''

          const legacyUserpk = Array.isArray(decodedToken.legacy_userpk)
            ? parseInt(decodedToken.legacy_userpk[0])
            : decodedToken.legacy_userpk ? parseInt(decodedToken.legacy_userpk) : 0

          const userFromToken = {
            email: rawEmail,
            firstName,
            lastName,
            sid: legacySid,
            legacyUserpk,
            name: firstName && lastName
              ? `${firstName} ${lastName}`.trim()
              : firstName || lastName || rawEmail || '',
          }
          localStorage.setItem('user', serializeUserForStorage(userFromToken))
        } else if (response.user) {
          localStorage.setItem('user', serializeUserForStorage(response.user))
        }
      }
      
      return response
    } catch (error: any) {
      throw error
    }
  },

  // Create new user
  createUser: async (data: CreateUserRequest): Promise<CreateUserResponse> => {
    try {
      const response = await api.post<CreateUserResponse>('/security/create-user', data)
      return response
    } catch (error: any) {
      throw error
    }
  },

  // Verify email with code
  verifyEmail: async (email: string, code: string): Promise<VerifyEmailResponse> => {
    try {
      const response = await api.post<VerifyEmailResponse>('/security/verify-email', {
        email,
        code,
      })
      
      // Store tokens if verification successful
      if (response.success && response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken)
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken)
        }
      }
      
      return response
    } catch (error: any) {
      throw error
    }
  },

  // Resend verification code
  resendVerificationCode: async (email: string): Promise<ResendVerificationCodeResponse> => {
    try {
      const response = await api.post<ResendVerificationCodeResponse>(
        '/security/resend-verification-code',
        { email }
      )
      return response
    } catch (error: any) {
      throw error
    }
  },

  // Check if user exists
  checkUser: async (email: string): Promise<CheckUserResponse> => {
    try {
      const response = await api.get<CheckUserResponse>(
        `/security/check-user?email=${encodeURIComponent(email)}`
      )
      return response
    } catch (error: any) {
      throw error
    }
  },

  // Refresh access token
  refreshToken: async (refreshToken: string, options?: { skipAuthRedirect?: boolean }): Promise<RefreshTokenResponse> => {
    try {
      const response = await api.post<RefreshTokenResponse>('/security/refresh-token', {
        refreshToken,
      }, options?.skipAuthRedirect ? { skipAuthRedirect: true } : undefined)
      
      // Update stored tokens
      if (response.success && response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken)
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken)
        }
        
        // Decode JWT to update user info
        const decodedToken = decodeJWT(response.accessToken)
        if (decodedToken) {
          const rawEmail = Array.isArray(decodedToken.email) ? decodedToken.email[0] : decodedToken.email
          const firstName = typeof decodedToken.legacy_fname === 'string' ? decodedToken.legacy_fname : ''
          const lastName = typeof decodedToken.legacy_lname === 'string' ? decodedToken.legacy_lname : ''
          const legacySid = Array.isArray(decodedToken.legacy_sid)
            ? decodedToken.legacy_sid[0]
            : decodedToken.legacy_sid || ''
          const legacyUserpk = Array.isArray(decodedToken.legacy_userpk)
            ? parseInt(decodedToken.legacy_userpk[0])
            : decodedToken.legacy_userpk ? parseInt(decodedToken.legacy_userpk) : 0

          const userFromToken = {
            email: rawEmail,
            firstName,
            lastName,
            sid: legacySid,
            legacyUserpk,
            name: firstName && lastName
              ? `${firstName} ${lastName}`.trim()
              : firstName || lastName || rawEmail || '',
          }
          localStorage.setItem('user', serializeUserForStorage(userFromToken))
        }
      }
      
      return response
    } catch (error: any) {
      throw error
    }
  },

  // Logout
  logout: async (): Promise<void> => {
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (refreshToken) {
        // Try to call logout endpoint, but don't fail if it returns 401
        try {
          await api.post('/security/logout', { refreshToken })
        } catch (logoutError: any) {
          // If logout returns 401, that's fine - token was already invalid
          if (logoutError?.response?.status !== 401) {
            console.error('Error during logout:', logoutError)
          }
        }
      }
    } catch (error: any) {
      console.error('Error during logout:', error)
    } finally {
      // Clear local storage regardless of API call result
      localStorage.removeItem('accessToken')
      localStorage.removeItem('refreshToken')
      localStorage.removeItem('user')
    }
  },

  // Reset password
  // Request password reset link (forgot password)
  requestPasswordResetLink: async (email: string, frontendUrl: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.post('/security/request-password-reset-link', {
        email,
        frontendUrl
      })
      return response
    } catch (error: any) {
      throw error
    }
  },

  completeAccountSetup: async (
    token: string,
    password: string,
    confirmPassword: string
  ): Promise<CompleteAccountSetupResponse> => {
    try {
      const response = await api.post<CompleteAccountSetupResponse>('/security/complete-account-setup', {
        token,
        password,
        confirmPassword,
      })
      return response
    } catch (error: any) {
      throw error
    }
  },

  // Reset password using token
  resetPasswordByToken: async (token: string, newPassword: string): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await api.post('/security/reset-password-by-token', {
        token,
        newPassword
      })
      return response
    } catch (error: any) {
      throw error
    }
  },

  // Change password
  changePassword: async (oldPassword: string, newPassword: string): Promise<void> => {
    try {
      const payload = {
        currentPassword: oldPassword,
        newPassword,
      }
      
      await api.post('/security/change-password', payload)
    } catch (error: any) {
      throw error
    }
  },

  // Get stored access token
  getAccessToken: (): string | null => {
    return localStorage.getItem('accessToken')
  },

  // Read legacy_userpk directly from the live JWT — works even for sessions created
  // before legacyUserpk was added to the stored user object.
  getLegacyUserpk: (): number => {
    const token = localStorage.getItem('accessToken')
    if (!token) return 0
    const decoded = decodeJWT(token)
    if (!decoded) return 0
    const n = parseInt(String(getDecodedClaim(decoded, 'legacy_userpk')))
    return isNaN(n) ? 0 : n
  },

  getLegacySiteId: (): number => {
    const token = localStorage.getItem('accessToken')
    if (!token) return 0
    const decoded = decodeJWT(token)
    if (!decoded) return 0
    const n = parseInt(String(getDecodedClaim(decoded, 'legacy_site_id')))
    return isNaN(n) ? 0 : n
  },

  getLegacyRegisterRoles: (): {
    legacy_campus_register: boolean
    legacy_district_register: boolean
    legacy_global_register: boolean
  } => {
    const token = localStorage.getItem('accessToken')
    if (!token) {
      return {
        legacy_campus_register: false,
        legacy_district_register: false,
        legacy_global_register: false,
      }
    }
    const decoded = decodeJWT(token)
    if (!decoded) {
      return {
        legacy_campus_register: false,
        legacy_district_register: false,
        legacy_global_register: false,
      }
    }
    return getLegacyRegisterRolesFromDecodedToken(decoded)
  },

  getCommunityAdminNotificationRole: (): boolean => {
    const token = localStorage.getItem('accessToken')
    if (!token) return false
    const decoded = decodeJWT(token)
    return decoded ? hasCommunityAdminNotificationRole(decoded) : false
  },

  // Get stored refresh token
  getRefreshToken: (): string | null => {
    return localStorage.getItem('refreshToken')
  },

  // Get stored user
  getUser: (): any | null => {
    const userStr = localStorage.getItem('user')
    if (userStr) {
      try {
        return JSON.parse(userStr)
      } catch {
        return null
      }
    }
    return null
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem('accessToken')
    return !!token
  },

  // Get user profile from API
  getProfile: async (): Promise<any> => {
    try {
      const response = await api.get('/security/profile')
      if (response.success && response.profile) {
        // Get existing user from JWT (has the name from legacy_fname/legacy_lname)
        const existingUser = authService.getUser()
        
        // DON'T overwrite the good JWT data with null profile data
        // Only use profile for email verification status
        const profile = response.profile
        
        // Keep the JWT user data (has the name), only update verification status
        if (existingUser && existingUser.name && existingUser.name !== existingUser.email) {
          const mergedUser = {
            ...existingUser,
            emailVerified: profile.isEmailVerified,
            isEmailVerified: profile.isEmailVerified
          }
          return { ...response, profile: mergedUser }
        }
        
        // Fallback if no JWT user data exists
        return response
      }
      return response
    } catch (error: any) {
      throw error
    }
  },

  // Validate SSO token
  validateSSOToken: async (token: string, _returnUrl?: string): Promise<SSOValidationResponse> => {
    try {
      const response = await api.post<SSOValidationResponse>(buildEmployeeSSOExchangeUrl(), {
        token,
      })
      
      // Store tokens if SSO validation successful
      if (response.success && response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken)
        if (response.refreshToken) {
          localStorage.setItem('refreshToken', response.refreshToken)
        }
        
        // Store user data (might be nested or at root level)
        const userData = response.user || {
          email: response.email,
          name: response.name,
          firstName: response.firstName,
          lastName: response.lastName,
          authenticatedVia: response.authenticatedVia || 'sso',
        }
        
        if (userData.email) {
          const decodedToken = decodeJWT(response.accessToken)
          const legacySid = decodedToken
            ? (Array.isArray(decodedToken.legacy_sid) ? decodedToken.legacy_sid[0] : decodedToken.legacy_sid || '')
            : ''
          const legacyUserpk = decodedToken
            ? (Array.isArray(decodedToken.legacy_userpk)
                ? parseInt(decodedToken.legacy_userpk[0])
                : decodedToken.legacy_userpk ? parseInt(decodedToken.legacy_userpk) : 0)
            : 0
          const userToStore = {
            email: userData.email,
            name: userData.name,
            firstName: userData.firstName,
            lastName: userData.lastName,
            authenticatedVia: userData.authenticatedVia,
            sid: legacySid,
            legacyUserpk,
          }
          localStorage.setItem('user', serializeUserForStorage(userToStore))
        }
      }
      
      return response
    } catch (error: any) {
      throw error
    }
  },

  // Request a backend-generated SSO redirect using bearer auth in the request header.
  generateSSORedirect: async (returnUrl?: string): Promise<SSORedirectResponse> => {
    return requestSSORedirect(returnUrl)
  },

  getRetailRedirect: async (returnUrl?: string): Promise<SSORedirectResponse> => {
    return requestSSORedirect(returnUrl)
  },

  goToRetail: async (returnUrl?: string): Promise<void> => {
    const { redirectUrl } = await authService.getRetailRedirect(returnUrl)
    window.location.assign(redirectUrl)
  },
}
